import React, { useState, useEffect } from "react";
import Text from "@Components/Text";
import PropTypes from "prop-types";
import { Dropdown, Icon } from "semantic-ui-react";
import Moment from "moment";
import Input from "@Components/Input";
import styles from "./styles.module.css";

const DateRangePicker = ({ startDate, endDate, handleInput, getMaxDate, isMonthMode }) => {
	const [rangeType, setRangeType] = useState("custom");

	useEffect(() => {
		const today = Moment().format("YYYY-MM-DD");
		// Sincronizar el endDate vacío si el estado inicial es 'today'
		if (!isMonthMode && startDate === today && !endDate) {
			handleInput({ target: { name: "endDate", value: today } });
		}
	}, []);

	// Efecto para sincronizar si el usuario limpia las fechas desde fuera
	useEffect(() => {
		if (!startDate && !endDate) {
			setRangeType((prev) => {
				if (prev !== "custom" && prev !== "") {
					return "";
				}
				return prev;
			});
		}
	}, [startDate, endDate]);

	const handleRangeChange = (e, { value }) => {
		setRangeType(value);

		let newStart = "";
		let newEnd = "";
		const today = Moment();

		if (isMonthMode) {
			switch (value) {
				case "thisMonth":
					newStart = today.format("YYYY-MM");
					newEnd = today.format("YYYY-MM");
					break;
				case "lastMonth":
					newStart = today.clone().subtract(1, "months").format("YYYY-MM");
					newEnd = today.clone().subtract(1, "months").format("YYYY-MM");
					break;
				case "last3Months":
					newStart = today.clone().subtract(3, "months").format("YYYY-MM");
					newEnd = today.format("YYYY-MM");
					break;
				case "thisYear":
					newStart = today.startOf("year").format("YYYY-MM");
					newEnd = today.endOf("year").format("YYYY-MM");
					break;
				default:
					// custom, do nothing
					break;
			}
		} else {
			switch (value) {
				case "today":
					newStart = today.format("YYYY-MM-DD");
					newEnd = today.format("YYYY-MM-DD");
					break;
				case "yesterday":
					newStart = today.clone().subtract(1, "days").format("YYYY-MM-DD");
					newEnd = today.clone().subtract(1, "days").format("YYYY-MM-DD");
					break;
				case "last7Days":
					newStart = today.clone().subtract(6, "days").format("YYYY-MM-DD");
					newEnd = today.format("YYYY-MM-DD");
					break;
				case "last30Days":
					newStart = today.clone().subtract(29, "days").format("YYYY-MM-DD");
					newEnd = today.format("YYYY-MM-DD");
					break;
				case "thisMonth":
					newStart = today.startOf("month").format("YYYY-MM-DD");
					newEnd = today.endOf("month").format("YYYY-MM-DD");
					break;
				case "lastMonth":
					newStart = today.clone().subtract(1, "months").startOf("month").format("YYYY-MM-DD");
					newEnd = today.clone().subtract(1, "months").endOf("month").format("YYYY-MM-DD");
					break;
				default:
					// custom, do nothing
					break;
			}
		}

		if (value !== "custom") {
			handleInput({ target: { name: "startDate", value: newStart } });
			handleInput({ target: { name: "endDate", value: newEnd } });
		}
	};

	const dateOptions = isMonthMode
		? [
				{ key: "thisMonth", text: "Este Mes", value: "thisMonth", icon: "calendar alternate outline" },
				{ key: "lastMonth", text: "Mes Pasado", value: "lastMonth", icon: "calendar minus outline" },
				{ key: "last3Months", text: "Últimos 3 Meses", value: "last3Months", icon: "history" },
				{ key: "thisYear", text: "Este Año", value: "thisYear", icon: "calendar" },
				{ key: "custom", text: "Personalizado", value: "custom", icon: "edit" },
		  ]
		: [
				{ key: "today", text: "Hoy", value: "today", icon: "sun outline" },
				{ key: "yesterday", text: "Ayer", value: "yesterday", icon: "history" },
				{ key: "last7Days", text: "Últimos 7 Días", value: "last7Days", icon: "calendar alternate outline" },
				{ key: "last30Days", text: "Últimos 30 Días", value: "last30Days", icon: "calendar outline" },
				{ key: "thisMonth", text: "Este Mes", value: "thisMonth", icon: "calendar check outline" },
				{ key: "lastMonth", text: "Mes Pasado", value: "lastMonth", icon: "calendar minus outline" },
				{ key: "custom", text: "Personalizado...", value: "custom", icon: "edit" },
		  ];

	return (
		<div className={styles.dateRangeContainer}>
			<div className={styles.dateFieldWrapper}>
				<Text className={styles.lookUpLabel}>Rango de Fechas:</Text>
				<Dropdown
					className={styles.modernDropdown}
					placeholder="Seleccionar rango"
					selection
					clearable
					options={dateOptions}
					value={rangeType}
					onChange={handleRangeChange}
				/>
			</div>

			<div className={styles.arrowWrapper}>
				<Icon name="arrow right" size="small" />
			</div>
			<div className={styles.dateFieldWrapper}>
				<Text className={styles.lookUpLabel}>
					Desde: {rangeType === "custom" && !endDate && <span className={styles.requiredAsterisk}>*</span>}
				</Text>
				<Input
					disabled={rangeType !== "custom"}
					type={isMonthMode ? "month" : "date"}
					icon="calendar minus"
					iconPosition="left"
					placeholder="Inicio"
					fluid
					size="mini"
					name="startDate"
					value={startDate}
					max={getMaxDate()}
					onChange={(e) => {
						handleInput(e);
						setRangeType("custom");
					}}
					className={styles.dateInputItem}
				/>
			</div>
			<div className={styles.arrowWrapper}>
				<Icon name="arrow right" size="small" />
			</div>
			<div className={styles.dateFieldWrapper}>
				<Text className={styles.lookUpLabel}>
					Hasta: {rangeType === "custom" && !startDate && <span className={styles.requiredAsterisk}>*</span>}
				</Text>
				<Input
					disabled={rangeType !== "custom"}
					type={isMonthMode ? "month" : "date"}
					icon="calendar plus"
					iconPosition="left"
					placeholder="Fin"
					fluid
					size="mini"
					name="endDate"
					value={endDate}
					min={startDate}
					max={getMaxDate()}
					onChange={(e) => {
						handleInput(e);
						setRangeType("custom");
					}}
					className={styles.dateInputItem}
				/>
			</div>
		</div>
	);
};

DateRangePicker.propTypes = {
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	handleInput: PropTypes.func.isRequired,
	getMaxDate: PropTypes.func.isRequired,
	isMonthMode: PropTypes.bool,
};

DateRangePicker.defaultProps = {
	startDate: "",
	endDate: "",
	isMonthMode: false,
};

export default DateRangePicker;
